import { ref, inject, provide, type Ref } from 'vue'
import { cloneDeep } from 'lodash-es'
import type useConfirmation from '@lib/composables/useConfirmation'
import { generateNewAgenda, generateNewEmptyGroup } from '@agenda-selector/utils/data.util'
import type { Agenda, AgendaGroup, AgendaItem } from '@agenda-selector/types/agenda.type'
import { ContentType } from '@agenda-selector/constants'
import { v4 as uuid } from 'uuid'
import type { SelectCollectionContent } from '@agenda-selector/types/common.type'
import { useContentWarning } from '@agenda-selector/composables/content-warning.use'
import type { CollectionSelectorProps } from '@collection-selector/types/collection-selector.type'

type StorePayload = {
  initialAgenda?: Agenda
  t: (key: string, values?: Record<string, string | number>) => string
  confirmation: ReturnType<typeof useConfirmation>
  selectCollectionContent: SelectCollectionContent
  attachTo?: HTMLElement | string
  zIndex?: number
  openSettings?: CollectionSelectorProps['openSettings']
}

type StoreData = {
  agenda: Ref<Agenda>
  expandedGroups: Ref<string[]>
  addGroup: (group?: Partial<AgendaGroup>) => AgendaGroup | undefined
  deleteGroup: (groupId: string, groupName: string) => void
  updateGroup: (group: Partial<Omit<AgendaGroup, 'id'>> & Pick<AgendaGroup, 'id'>) => void
  updateGroups: (groups: AgendaGroup[]) => void
  updateItems: (groupId: string, items: AgendaItem[]) => void
  updateAgendaName: (name: string) => void
  updateGroupName: (groupId: string, name: string) => void
  deleteItem: (groupId: string, item: AgendaItem) => void
  editItem: (groupId: string, item: AgendaItem, filters?: Record<string, any>) => Promise<void>
  addItem: (groupId: string, filters?: Record<string, any>) => Promise<AgendaItem | undefined>
  moveItemToGroup: (fromGroup: string, toGroup: string, item: AgendaItem) => void
  selectCollectionContent: SelectCollectionContent
  childModalZIndex?: number
  contentWarning: ReturnType<typeof useContentWarning>
}

const StoreKey = Symbol('AgendaSelectorStore')

export function provideAppStore({
  initialAgenda,
  t,
  confirmation,
  selectCollectionContent,
  attachTo,
  zIndex,
  openSettings,
}: StorePayload) {
  const agenda = ref<Agenda>({ ...generateNewAgenda(t), ...(cloneDeep(initialAgenda) ?? {}) })
  const expandedGroups = ref<string[]>(agenda.value.groups.map((group) => group.id))
  const childModalZIndex = zIndex !== undefined ? zIndex + 1 : undefined
  const contentWarning = useContentWarning()

  const expandGroup = (groupId: string) => {
    if (!expandedGroups.value.includes(groupId)) {
      expandedGroups.value.push(groupId)
    }
  }

  const setAgenda = (newAgenda: Agenda) => {
    agenda.value = newAgenda
  }

  const updateGroup = (group: Partial<Omit<AgendaGroup, 'id'>> & Pick<AgendaGroup, 'id'>) => {
    setAgenda({
      ...agenda.value,
      groups: [
        ...agenda.value.groups.map((_group) =>
          _group.id === group.id
            ? {
                ..._group,
                ...group,
              }
            : _group,
        ),
      ],
    })
  }

  const createGroup = (group?: Partial<AgendaGroup>) => ({ ...generateNewEmptyGroup(t), ...(group ?? {}) })

  const addGroup = (group?: Partial<AgendaGroup>): AgendaGroup | undefined => {
    const newGroup = createGroup(group)

    setAgenda({
      ...agenda.value,
      groups: [...agenda.value.groups, newGroup],
    })

    expandGroup(newGroup.id)

    return newGroup
  }

  const deleteGroup = (groupId: string, groupName: string) => {
    confirmation.danger({
      zIndex: childModalZIndex,
      hasExit: false,
      title: t('canvasUI.agendaSelector.deleteItemModal.title', { name: groupName }),
      content: t('canvasUI.agendaSelector.deleteItemModal.content', { name: groupName }),
      confirmText: t('canvasUI.agendaSelector.deleteItemModal.confirm'),
      cancelText: t('canvasUI.agendaSelector.deleteItemModal.cancel'),
      onConfirm: () => {
        const groups = agenda.value.groups.filter((group) => group.id !== groupId)

        if (groups.length === 0) {
          const newGroup = createGroup()

          groups.push(newGroup)
          expandGroup(newGroup.id)
        }

        setAgenda({
          ...agenda.value,
          groups,
        })
      },
    })
  }

  const updateGroups = (groups: AgendaGroup[]) => {
    setAgenda({
      ...agenda.value,
      groups,
    })
  }

  const updateItems = (groupId: string, items: AgendaItem[]) => {
    setAgenda({
      ...agenda.value,
      groups: agenda.value.groups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              items,
            }
          : group,
      ),
    })
  }

  const updateAgendaName = (name: string) => {
    setAgenda({
      ...agenda.value,
      name: name.trim() || t('canvasUI.agendaSelector.defaultAgendaName'),
    })
  }

  const updateGroupName = (groupId: string, name: string) => {
    setAgenda({
      ...agenda.value,
      groups: agenda.value.groups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              name: name.trim() || t('canvasUI.agendaSelector.defaultGroupName'),
            }
          : group,
      ),
    })
  }

  const deleteItem = (groupId: string, item: AgendaItem) => {
    confirmation.danger({
      zIndex: childModalZIndex,
      title: t('canvasUI.agendaSelector.deleteItemModal.title', {
        name: item.content.name,
      }),
      content: t('canvasUI.agendaSelector.deleteItemModal.content', {
        name: item.content.name,
      }),
      confirmText: t('canvasUI.agendaSelector.deleteItemModal.confirm'),
      cancelText: t('canvasUI.agendaSelector.deleteItemModal.cancel'),
      hasExit: false,
      onConfirm: async () => {
        const groups = agenda.value.groups.map((group) => {
          if (group.id === groupId) {
            const items = group.items.filter((i) => i.id !== item.id)

            return {
              ...group,
              items,
            }
          }

          return group
        })

        updateGroups(groups)
      },
    })
  }

  const findItem = (groupId: string, itemId: string) => {
    return agenda.value.groups.find((group) => group.id === groupId)?.items.find((item) => item.id === itemId)
  }

  const findGroup = (groupId: string) => agenda.value.groups.find((group) => group.id === groupId)

  const addItem = async (groupId: string, filters?: Record<string, any>) => {
    const group = findGroup(groupId)

    if (!group) {
      return
    }

    const data = await selectCollectionContent({
      attach_to: attachTo,
      initial_metadata_filters: filters,
      open_settings: openSettings,
    })

    if (!data || data.user_action === 'cancelled' || !data.content) {
      return
    }

    const updatedItem = { id: uuid(), content: data.content, type: ContentType.Collection }

    updateGroup({
      id: groupId,
      items: [...group.items, updatedItem],
    })

    expandGroup(groupId)
    contentWarning.validateCollectionPlayerContent(data.content)

    return updatedItem
  }

  const editItem = async (groupId: string, item: AgendaItem, filters?: Record<string, any>) => {
    const group = findGroup(groupId)
    const foundItem = findItem(groupId, item.id)

    if (!group || !foundItem) {
      return
    }

    const data = await selectCollectionContent({
      initial_data: foundItem.content,
      attach_to: attachTo,
      initial_metadata_filters: filters,
      open_settings: openSettings,
    })

    if (!data || data.user_action === 'cancelled' || !data.content) {
      return
    }

    const newContent = data.content

    updateGroup({
      id: groupId,
      items: group.items.map((_item) => {
        if (_item.id === item.id) {
          return {
            ..._item,
            content: newContent,
          }
        } else {
          return _item
        }
      }),
    })

    contentWarning.validateCollectionPlayerContent(newContent)
  }

  const moveItemToGroup = (fromGroup: string, toGroup: string, item: AgendaItem) => {
    updateGroups(
      agenda.value.groups.map((group) => {
        if (group.id === fromGroup) {
          return {
            ...group,
            items: group.items.filter((s) => s.id !== item.id),
          }
        }

        if (group.id === toGroup) {
          return {
            ...group,
            items: [...group.items, item],
          }
        }

        return group
      }),
    )
  }

  const storeData: StoreData = {
    agenda,
    expandedGroups,
    childModalZIndex,
    selectCollectionContent,
    contentWarning,
    addGroup,
    deleteGroup,
    updateGroup,
    updateGroups,
    updateItems,
    updateAgendaName,
    updateGroupName,
    deleteItem,
    addItem,
    editItem,
    moveItemToGroup,
  }

  provide<StoreData>(StoreKey, storeData)

  return storeData
}

export function useAppStore() {
  const storeData = inject<StoreData>(StoreKey)

  if (!storeData) {
    throw new Error('useAppStore() is called without provider.')
  }

  return storeData
}
